import * as classNames from 'classnames';
import { EVisibility, ILeadNote } from 'common/interfaces/provider';
import { UserType } from 'common/interfaces/user';
import { IconButton } from 'components/action';
import { SavingIndicator, SavingIndicatorStatus } from 'components/indicator';
import { action, computed } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as moment from 'moment';
import * as React from 'react';
import { IAppStore } from 'stores/AllStores';

// Styles
const s = require('../LeadNotes.css');
const editIcon = require('assets/pen icon.svg');
const visibilityIcon = require('assets/visibility button.svg');

//Props
export interface ILeadNoteDetailsProps {
  noteInfo: ILeadNote;
  store?: IAppStore;
}

// Component
@inject('store')
@observer
export class LeadNoteDetails extends React.Component<
  ILeadNoteDetailsProps,
  {}
> {
  constructor(props: ILeadNoteDetailsProps) {
    super(props);
  }

  @computed private get isContentBeingEdited(): boolean {
    return this.props.store.lead.leadEditingNote.id === this.props.noteInfo.id;
  }

  @computed private get minutesSince(): boolean {
    const { noteInfo } = this.props;

    const editMoment = moment(noteInfo.updatedAt || noteInfo.createdAt);
    const millisecondsSince = moment().diff(editMoment);
    const minutesSince = moment.duration(millisecondsSince).asMinutes();

    return minutesSince <= 60;
  }

  @computed private get isAuthor(): boolean {
    const { store, noteInfo } = this.props;
    return (
      store.user.id === (noteInfo.createdByUser.id || noteInfo.updatedByUser.id)
    );
  }

  @computed private get isAdmin(): boolean {
    return this.props.store.user.userType === UserType.Admin;
  }

  @computed private get isEditable(): boolean {
    return (this.isAuthor && this.minutesSince) || this.isAdmin;
  }

  @computed private get isHideable(): boolean {
    return (this.isAuthor && this.minutesSince) || this.isAdmin;
  }

  @computed private get isCreatingNote(): boolean {
    const { store, noteInfo } = this.props;
    return store.lead.leadEditingNote.id === noteInfo.id;
  }

  @computed private get isHidingNote(): boolean {
    const { store, noteInfo } = this.props;
    return store.lead.leadHidingNote.id === noteInfo.id;
  }

  public render() {
    const { noteInfo } = this.props;
    const { leadEditingNote, leadHidingNote } = this.props.store.lead;

    return (
      <div className={s.noteDetails}>
        {this.isContentBeingEdited && (
          <div className={s.detail}>
            <div className={s.left}>
              <SavingIndicator
                align="left"
                labels={{
                  saving: 'Saving changes...',
                  error: 'Could not update the note, try again',
                }}
                status={
                  leadEditingNote.error
                    ? SavingIndicatorStatus.Error
                    : leadEditingNote.isSaving
                    ? SavingIndicatorStatus.Saving
                    : SavingIndicatorStatus.None
                }
              />
            </div>

            <div
              className={classNames(s.right, s.saveButtons, {
                [s.saving]: leadEditingNote.isSaving,
              })}
            >
              <button
                className={classNames('boxButton', 'boxButtonImportant')}
                onClick={
                  leadEditingNote.isSaving
                    ? undefined
                    : this.handleEditNoteCancel
                }
              >
                Cancel
              </button>
              <button
                className={classNames('boxButton', s.publish)}
                onClick={
                  leadEditingNote.isSaving
                    ? undefined
                    : this.handleEditNoteUpdate
                }
              >
                Publish
              </button>
            </div>
          </div>
        )}

        {this.isContentBeingEdited === false && (
          <div
            className={classNames(s.detail, {
              [s.withIconButton]: this.isEditable,
            })}
          >
            <div className={s.left}>
              <SavingIndicator
                align="left"
                labels={{
                  saving:
                    noteInfo.hidden === EVisibility.Hidden
                      ? 'Showing...'
                      : 'Hiding...',
                  error: `Could not ${
                    noteInfo.hidden === EVisibility.Hidden ? 'show' : 'hide'
                  } the note, try again`,
                }}
                status={
                  this.isHidingNote
                    ? leadHidingNote.error
                      ? SavingIndicatorStatus.Error
                      : leadHidingNote.isSaving
                      ? SavingIndicatorStatus.Saving
                      : SavingIndicatorStatus.None
                    : SavingIndicatorStatus.None
                }
              />
            </div>

            <div className={s.right}>
              {this.isHideable && (
                <IconButton
                  className={classNames(s.button, s.iconButton, s.hide, {
                    [s.hidden]: noteInfo.hidden === 1,
                  })}
                  onClick={this.handleLeadNoteHide}
                  removePadding={true}
                  label={
                    noteInfo.hidden === EVisibility.Hidden
                      ? 'Recover note'
                      : 'Delete note'
                  }
                  icon={visibilityIcon}
                />
              )}

              {this.isEditable && this.isHideable && (
                <IconButton
                  className={classNames(s.button, s.iconButton, s.edit)}
                  onClick={this.handleLeadNoteStartEditing}
                  removePadding={true}
                  label="Edit note"
                  icon={editIcon}
                />
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  @action private handleLeadNoteHide = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    const { store, noteInfo } = this.props;

    if (this.isHideable) {
      try {
        this.props.store.lead.leadHidingNote = {
          id: noteInfo.id,
          isSaving: true,
          error: false,
        };

        const update: ILeadNote = await store.lead.updateLeadNote(noteInfo.id, {
          hidden:
            noteInfo.hidden === EVisibility.Visible
              ? EVisibility.Hidden
              : EVisibility.Visible,
        });

        this.props.store.lead.leadHidingNote = {
          id: null,
          isSaving: false,
          error: false,
        };

        const leadDetailsIndex = store.lead.leadsDetailsIds.indexOf(
          store.lead.openLeadId
        );

        let leadsNoteIndex = -1;
        store.lead.leadsDetails[leadDetailsIndex].notes.some(
          (note, noteIndex) => {
            if (note.id === noteInfo.id) {
              leadsNoteIndex = noteIndex;
              return true;
            }

            return false;
          }
        );

        if (leadsNoteIndex !== -1) {
          store.lead.leadsDetails[leadDetailsIndex].notes[
            leadsNoteIndex
          ].hidden = noteInfo.hidden ? EVisibility.Visible : EVisibility.Hidden;
        }
      } catch (err) {
        this.props.store.lead.leadHidingNote = {
          ...this.props.store.lead.leadHidingNote,
          isSaving: false,
          error: true,
        };
        console.error(err);
        throw err;
      }
    }
  };

  @action private handleLeadNoteStartEditing = (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (this.isEditable) {
      this.props.store.lead.leadEditingNote = {
        id: this.props.noteInfo.id,
        isSaving: false,
        error: false,
        html: '',
        text: '',
      };
    }
  };

  @action private handleEditNoteCancel = async (): Promise<boolean> => {
    this.props.store.lead.leadEditingNote.id = null;
    this.props.store.lead.leadEditingNote.isSaving = false;
    this.props.store.lead.leadEditingNote.error = false;
    this.props.store.lead.leadEditingNote.html = '';
    this.props.store.lead.leadEditingNote.text = '';
    return true;
  };

  @action private handleEditNoteUpdate = async (): Promise<boolean> => {
    const { store, noteInfo } = this.props;

    if (store.lead.leadEditingNote.text.trim().length <= 0) {
      return await this.handleEditNoteCancel();
    } else {
      try {
        store.lead.leadEditingNote.isSaving = true;

        // Load lead of ID
        const update: ILeadNote = await store.lead.updateLeadNote(noteInfo.id, {
          comment: store.lead.leadEditingNote.text,
          providerId: noteInfo.providerId,
          hidden: EVisibility.Visible,
        });

        // Hide edit section
        store.lead.leadEditingNote = {
          id: null,
          isSaving: false,
          error: false,
          html: '',
          text: '',
        };

        // Update note content
        const leadDetailsIndex = store.lead.leadsDetailsIds.indexOf(
          store.lead.openLeadId
        );

        let leadsNoteIndex = -1;
        store.lead.leadsDetails[leadDetailsIndex].notes.some(
          (note, noteIndex) => {
            if (note.id === noteInfo.id) {
              leadsNoteIndex = noteIndex;
              return true;
            }

            return false;
          }
        );

        if (leadDetailsIndex !== -1 && leadsNoteIndex !== -1) {
          store.lead.leadsDetails[leadDetailsIndex].notes[
            leadsNoteIndex
          ].comment = update.comment;
          store.lead.leadsDetails[leadDetailsIndex].notes[
            leadsNoteIndex
          ].updatedAt = moment().format();
          store.lead.leadsDetails[leadDetailsIndex].notes[
            leadsNoteIndex
          ].updatedByUser = {
            id: store.user.id,
            name: store.user.name,
            username: store.user.username,
            email: store.user.email,
          };
        }

        // Return success
        return true;
      } catch (err) {
        store.lead.leadEditingNote = {
          ...store.lead.leadEditingNote,
          isSaving: false,
          error: true,
        };
        console.error(err);
        throw err;
      }
    }
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/routes/sales/Lead/Notes/Note/LeadNoteDetails.tsx